/**
 * LicensorConsolePricingTab — PricingTabGrid + SubTabButton for LicensorConsole.
 * Extracted from LicensorConsole.jsx to reduce file complexity.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { erpToast }           from './erp/ERPToast';
import { PricingFormModal }   from './LicensePricingManager';
import TierPrivilegesEditor from './TierPrivilegesEditor';
import { useTierList } from '../lib/useTierList';
import UAMSMasterManager      from './UAMSMasterManager';
import TierIntegrityPanel     from './TierIntegrityPanel';
import IndustryMappingPanel   from './IndustryMappingPanel';
import LicensePricingManager  from './LicensePricingManager';
import { SectionHeader, Section, DataTable, Skeleton, ErrorBanner } from './LicensorConsolePrimitives';
import { API, apiFetch } from '../lib/api';

export function SubTabButton({ active, onClick, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      style={{ padding: '6px 14px', background: active ? '#0f172a' : 'transparent', color: active ? '#f8fafc' : '#64748b', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em' }}>
      {label}
    </button>
  );
}

export function PricingTabGrid({ forceTab = null, pricingSubTab = null } = {}) {
  const hidePricingSub = forceTab === 'tier-config';
  const initialSubTab  = pricingSubTab || (forceTab === 'tier-config' ? 'industry' : 'pricing');
  const [subTab,          setSubTab]          = useState(initialSubTab);
  const [data,            setData]            = useState({ pricing: [], tierConfig: [] });
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [editing,         setEditing]         = useState(null);
  const [industries,      setIndustries]      = useState([]);
  const [selectedIndustry,setSelectedIndustry]= useState('*');
  const [syncStatus,      setSyncStatus]      = useState(null); // null | 'running' | { running, err, at }

  // Dynamic tier ordering — replaces static TIER_ORDER array
  const { tiers: dynamicTierOrder } = useTierList();

  useEffect(() => {
    if (pricingSubTab) setSubTab(pricingSubTab);
    else if (forceTab === 'tier-config') setSubTab(prev => (prev === 'pricing' ? 'industry' : prev));
  }, [forceTab, pricingSubTab]);

  useEffect(() => {
    const token = sessionStorage.getItem('erp_security_token');
    fetch(`${API}/uams/industries`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.json()).then(d => setIndustries(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const applyHint = (sub) => {
      if (['pricing', 'uams', 'privileges', 'integrity', 'industry'].includes(sub)) setSubTab(sub);
    };
    const ts     = parseInt(sessionStorage.getItem('erp-nav-subtab-ts') || '0', 10);
    const hinted = sessionStorage.getItem('erp-nav-subtab');
    if (hinted && Date.now() - ts < 3000) {
      applyHint(hinted);
      sessionStorage.removeItem('erp-nav-subtab');
      sessionStorage.removeItem('erp-nav-subtab-ts');
    }
    const handler = (e) => applyHint(e.detail?.subTab);
    window.addEventListener('erp-navigate', handler);
    return () => window.removeEventListener('erp-navigate', handler);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`${API}/licensor/pricing`).then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncAllDBs = useCallback(async () => {
    setSyncStatus('running');
    try {
      const res = await apiFetch(`${API}/uams/propagate-to-all`, { method: 'POST' });
      const d   = await res.json();
      setSyncStatus({ running: !!d.accepted, err: false, at: new Date().toLocaleTimeString() });
    } catch (_) {
      setSyncStatus({ running: false, err: true, at: new Date().toLocaleTimeString() });
    }
  }, []);

  if (loading) return <Skeleton label="Loading pricing..." />;
  if (error)   return <ErrorBanner error={error} onRetry={load} />;

  const activeTiers  = data.pricing.filter(p => p.IsActive);
  // Order by dynamicTierOrder (from DB), then append any tiers not yet in that list
  const orderedTiers = dynamicTierOrder
    .map(name => activeTiers.find(p => p.LicenseTier === name))
    .filter(Boolean);
  activeTiers.forEach(t => { if (!dynamicTierOrder.includes(t.LicenseTier)) orderedTiers.push(t); });

  const showSyncBar = ['uams', 'privileges', 'integrity', 'industry'].includes(subTab);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader title="Pricing & Tier Configuration" badge={`${orderedTiers.length} active tiers`} onRefresh={load} />

      {/* ── Sync All DBs bar — visible on UAMS, privileges, integrity, industry sub-tabs ── */}
      {showSyncBar && (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px',
          background:'var(--surface-2,#f8fafc)', border:'1px solid var(--border)',
          borderRadius:8, fontSize:'0.78rem' }}>
          <RefreshCw size={11} color="var(--text-muted)" />
          <span style={{ color:'var(--text-secondary)', flex:1 }}>
            {syncStatus === null && 'Push tier privileges + UAMS entitlements to all Template DBs and Client DBs.'}
            {syncStatus === 'running' && <span style={{ color:'#f59e0b' }}>Submitting…</span>}
            {syncStatus && syncStatus !== 'running' && syncStatus.running && (
              <span style={{ color:'#f59e0b', display:'flex', alignItems:'center', gap:4 }}>
                <RefreshCw size={10} className="spin" /> Propagating to all databases in background…
              </span>
            )}
            {syncStatus && syncStatus !== 'running' && !syncStatus.running && !syncStatus.err && (
              <span style={{ color:'#22c55e', display:'flex', alignItems:'center', gap:4 }}>
                <CheckCircle size={10} /> Accepted at {syncStatus.at} — running in background
              </span>
            )}
            {syncStatus && syncStatus !== 'running' && syncStatus.err && (
              <span style={{ color:'#ef4444', display:'flex', alignItems:'center', gap:4 }}>
                <AlertTriangle size={10} /> Sync failed — check server logs
              </span>
            )}
          </span>
          <button className="erp-btn erp-btn-primary"
            onClick={syncAllDBs}
            disabled={syncStatus === 'running' || (syncStatus && syncStatus !== 'running' && syncStatus.running)}
            data-testid="licensor-sync-all-dbs"
            style={{ whiteSpace:'nowrap', fontSize:'0.78rem', padding:'4px 10px', gap:5 }}>
            <RefreshCw size={11} className={syncStatus === 'running' ? 'spin' : ''} />
            {(syncStatus === 'running' || (syncStatus && syncStatus !== 'running' && syncStatus.running)) ? 'Syncing…' : 'Sync All DBs'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid #e2e8f0', paddingBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {!hidePricingSub && <SubTabButton active={subTab === 'pricing'} onClick={() => setSubTab('pricing')} label="Pricing" testId="pricing-subtab-pricing" />}

        {(subTab === 'integrity' || subTab === 'privileges') && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <label style={{ fontSize: 12, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.06 }}>Industry filter</label>
            <select className="form-input" value={selectedIndustry} onChange={e => setSelectedIndustry(e.target.value)} data-testid="pricing-industry-filter" style={{ minWidth: 220 }}>
              <option value="*">— All industries (baseline) —</option>
              {industries.map(i => <option key={i.IndustryCode} value={i.IndustryCode}>{i.IndustryName}{i.Category ? ` · ${i.Category}` : ''}</option>)}
            </select>
          </div>
        )}
      </div>

      {subTab === 'pricing' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${orderedTiers.length}, 1fr)`, gap: 12 }}>
            {orderedTiers.map(p => {
              const isFree  = p.IsFree === true || p.IsFree === 1;
              const isSales = p.IsSalesDefined === true || p.IsSalesDefined === 1;
              const color   = isFree ? '#10b981' : isSales ? '#8b5cf6' : '#3b82f6';
              return (
                <div key={p.PricingId} className="card" style={{ padding: 8, borderTop: `3px solid ${color}` }}>
                  <div style={{ fontSize: 12, color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                    {isFree ? 'FREE' : isSales ? 'SALES-DEFINED' : 'PAID'}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 900, color: '#0f172a', marginBottom: 6 }}>{p.LicenseTier}</div>
                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, minHeight: 36, marginBottom: 10 }}>{p.TierDescription || '—'}</div>
                  {p.DbModel && (
                    <div style={{ display:'inline-block', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:4, padding:'2px 7px', fontSize:10, fontWeight:700, letterSpacing:'0.06em', color:'#475569', marginBottom:8 }}>
                      {p.DbModel}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                    <div><strong>{isSales ? 'Custom quote' : `₹ ${Number(p.BasePrice).toLocaleString()}`}</strong> <span style={{ color: '#94a3b8' }}>base</span></div>
                    {!isFree && !isSales && (
                      <>
                        <div>+ ₹ {Number(p.PricePerUser || 0).toLocaleString()} <span style={{ color: '#94a3b8' }}>/ user</span></div>
                        <div>+ ₹ {Number(p.PricePerSystem || 0).toLocaleString()} <span style={{ color: '#94a3b8' }}>/ system</span></div>
                      </>
                    )}
                    {isFree && p.MaxDays && <div style={{ color: '#dc2626', fontWeight: 700 }}>⏱ Limited to {p.MaxDays} days</div>}
                  </div>
                  <button className="erp-btn erp-btn-ghost" style={{ marginTop: 10, fontSize: 12, padding: '4px 8px' }} onClick={() => setEditing(p)} data-testid={`tier-edit-${p.LicenseTier}`}>Edit</button>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: -4 }}><LicensePricingManager /></div>

          {data.tierConfig?.length > 0 && (
            <div style={{ marginTop: -4 }}>
              <Section title="WebApp.tbl_license_tier_config" sub="Master tier configuration (features, limits, database model) · Lite is the default for every new signup">
                <DataTable rows={data.tierConfig} columns={['tier_code','tier_name','license_type','description','base_price','currency','max_users','max_companies','max_devices','database_model','is_active']} />
              </Section>
            </div>
          )}
        </>
      )}

      {subTab === 'uams'       && <UAMSMasterManager />}
      {subTab === 'privileges' && <TierPrivilegesEditor industryFilter={selectedIndustry} />}
      {subTab === 'integrity'  && <TierIntegrityPanel industryFilter={selectedIndustry} />}
      {subTab === 'industry'   && <IndustryMappingPanel initialIndustry={selectedIndustry !== '*' ? selectedIndustry : null} />}

      {editing && (
        <PricingFormModal
          mode="edit"
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
